import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { TabPanel, TabView } from 'primereact/tabview';
import Alarms from './components/alarms/alarms';
import Tests from './components/tests/tests';
import { Ingestion } from './components/ingestion/ingestion';
import { Button } from 'primereact/button';
import { useToast } from '../../../context/ToastProvider';
import { apiServices } from '../../../services/apiServices';
import { MarsinetContext } from '../../../context/marsinetProvider';
import { AvailableResourceActions, can } from '@marsinet/commons';
import { useLocation, useNavigate } from 'react-router-dom';

export function Maintenance() {
  const { t } = useTranslation();
  const { showToast: show } = useToast();

  const [updateResize, setUpdateResize] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [selectedProducts, setSelectedProducts] = useState(null);
  const { role } = useContext(MarsinetContext);
  const navigate = useNavigate();
  const hasMaintenancePageAccess =
    role && can(role, AvailableResourceActions.ingestions.read_section);
  const canDownloadIngestionZips =
    role && can(role, AvailableResourceActions.ingestions.download_zips);
  const location = useLocation();

  const [initialSerialFilter, setInitialSerialFilter] = useState<string | null>(null);
  const [preselectedDeviceId, setPreselectedDeviceId] = useState<string | null>(null);

  useEffect(() => {
    const navState = location.state as
      | { tab?: string; serial?: string; deviceId?: string }
      | null;
    if (!navState) return;

    if (navState.tab === 'ingestion') {
      setActiveIndex(0);
      setInitialSerialFilter(navState.serial ?? null);
    } else if (navState.tab === 'alarms') {
      setActiveIndex(2);
      setPreselectedDeviceId(navState.deviceId ?? null);
    }

    // clear so a back-nav or refresh doesn't re-apply it
    navigate(location.pathname, { replace: true, state: null });
  }, []);

  useEffect(() => {
    if (role && !hasMaintenancePageAccess) {
      navigate('/authenticated');
    }
  }, [role, hasMaintenancePageAccess, navigate]);

  useEffect(() => {
    window.addEventListener('resize', updateResizeGraph);
    return () => window.removeEventListener('resize', updateResizeGraph);
  }, [updateResize]);

  const updateResizeGraph = () => {
    setUpdateResize(!updateResize);
  };

  async function downloadSelectedData() {
    try {
      show(
        'info',
        t('messages.info'),
        t('messages.ingestion.download_multiple_files')
      );
      const files = selectedProducts.map((product) => product.zip_data);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any =
        await apiServices.ingestionApi.ingestionControllerDownloadGroupFiles(
          { files },
          {
            responseType: 'blob',
          }
        );

      if (response.data) {
        const blob = new Blob([response.data], { type: 'application/zip' });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = 'download.zip';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        show(
          'success',
          t('messages.success'),
          t('messages.ingestion.dowload_multiple_files_success')
        );
      } else {
        show('error', t('messages.error'), t('messages.ingestion.empty_file'));
      }
    } catch (_error) {
      show(
        'error',
        t('messages.error'),
        t('messages.ingestion.dowload_multiple_files_error')
      );
    }
  }

  const changeTabView = (index: number) => {
    setActiveIndex(index);
  };

  return (
    <div className="col-12 xl:pl-7 xl:pr-7 ">
      <div className="col-12 ">
        <div className="surface-card p-3 shadow-3 border-round-xl flex justify-content-between align-items-center mb-5">
          <div className="font-medium text-lg sm:text-xl md:text-2xl text-900">
            {t('pages.maintenance.title')}
          </div>
          {selectedProducts &&
            selectedProducts.length > 0 &&
            canDownloadIngestionZips ? (
            <Button
              icon="pi pi-download"
              label={t('pages.ingestions.downloadFiles')}
              onClick={() => downloadSelectedData()}
              severity="secondary"
              aria-label="Añadir"
            />
          ) : null}
        </div>
      </div>
      <div className="col-12 grid mx-0">
        <div className="surface-card border-round-xl shadow-3	pt-5 px-3 col-12">
          <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => changeTabView(e.index)}
          >
            <TabPanel header={t('pages.maintenance.ingestion.title')}>
              <Ingestion
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
                initialSerialFilter={initialSerialFilter}
              />
            </TabPanel>
            <TabPanel header={t('pages.maintenance.test.title')}>
              <Tests
                selectedProducts={selectedProducts}
                setSelectedProducts={setSelectedProducts}
              />
            </TabPanel>
            <TabPanel header={t('pages.maintenance.alarms.title')}>
              <Alarms preselectedDeviceId={preselectedDeviceId} />
            </TabPanel>
          </TabView>
        </div>
      </div>
    </div>
  );
}

export default Maintenance;
